import React from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import moment from 'moment';
import { Link } from 'react-router';
import { Table, Icon, Popup, Button, Modal, Image, Header, Label, Divider } from 'semantic-ui-react';
import NotAvailableComponent from 'dyzio-shared-components/lib/reports/not-available-component';
import { compose } from 'recompose';
import { TiktokIcon } from '../dyzio-icons';
import { sf } from '../../utils/formatting';
import {
  TIKTOK,
  VK,
  BLOG,
  SNAPCHAT,
  INSTAGRAM,
  TWITTER,
  YOUTUBE,
  FACEBOOK,
  TELEGRAM,
} from '../../utils/static-configuration';

import UploadModal from '../../containers/upload-modal';
import TargetLink from '../target-link';
import PrivateDataComponent from '../private-data-component';

import FacebookImpressions from '../facebook-impressions';
import YoutubeImpressions from '../youtube-impressions';
import InstagramImpressions from '../instagram-impressions';

import FacebookInsightsModal from '../facebook-insights-modal';
import YoutubeInsightsModal from '../youtube-insights-modal';
import InstagramInsightsModal from '../instagram-insights-modal';
import InstagramStoryModal from '../../containers/instagram-story-modal';

import DeleteAssetDialog from '../../components/delete-asset-dialog';
import FavouriteStar from '../../components/favourite-star';

import NoAssetsCard from '../../components/no-assets-card';

import { isAutoAdded, isStory } from '../../utils/automation-utils';
import { transformHashtagstIntoArray } from '../../utils/hashtags-utils';

import hasRESTClient from '../../hocs/has-rest-client';
import hasJSONAPIClient from '../../hocs/has-jsonapi-client';

import VKAsset from './assets-type/vk';
import SnapChatAsset from './assets-type/snapchat';
import BlogAsset from './assets-type/blog';

const TIKTOK_THUMBNAIL_RETRY = 2;

const FETCHING = 'FETCHING';

const PLATFORMS_WITHOUT_ANALYTICS = [
  BLOG,
  VK,
  SNAPCHAT,
  TIKTOK,
  TELEGRAM,
];

const assetTableStyles = {
  padding: 0,
};

const removeActionStyles = {
  cursor: 'pointer',
};

const isGraphQLEmpty = obj => obj && (obj.length === 1) && (obj[0] === null);

const getViews = asset => {
  //
  if (_.isNil(_.get(asset, 'latestStatistic.viewCount'))) {
    return <NotAvailableComponent metricName="views" assetType="image" />;
  }

  if (_.get(asset, 'isReel') === true) {
    return sf(_.get(asset, 'latestStatistic.playCount', 0));
  }

  return sf(_.get(asset, 'latestStatistic.viewCount', 0));
};

const fetchingData = () => (
  <Icon name="hourglass empty" color="orange" />
);

const dyzioRedStyle = { color: '#ef3a50' };
const dyzioOrangeStyle = { color: 'orange' };

const assetErrorRed = () => (
  <Icon name="warning sign" style={dyzioRedStyle} />
);

const assetError = () => (
  <Icon name="warning sign" style={dyzioOrangeStyle} />
);

const assetWarning = () => (
  <Icon name="question circle" style={dyzioOrangeStyle} />
);

const FacebookPostType = ({ asset }) => {
  let pt;
  pt = asset.postType;

  if (!pt) {
    pt = asset.isVideo ? 'video' : 'photo';
  }

  const types = {
    video: <Icon name="youtube play" color="blue" />,
    photo: <Icon name="image" color="blue" />,
    post: <Icon name="comment outline" color="blue" />,
    pagepost: <Icon name="comment outline" color="blue" />,
  };
  return types[pt];
};

const UploadSupportingFiles = ({ value, handler }) => (
  <Button
    size="mini"
    inverted
    circular
    onClick={() => handler(value)}
    icon="plus"
    color="green"
  />
);

const S3_PREFIX = 'https://s3-eu-west-1.amazonaws.com/discussions-uploads-production';
const imageWrapperStyle = {
  maxHeight: 500,
  overflowY: 'scroll',
  marginBottom: 10,
};

const DeleteButton = props => <Button
  size="mini"
  key={props.value}
  onClick={props.handler}
  value={props.value}
  compact
  circular
  icon="delete"
/>;

DeleteButton.propTypes = {
  handler: PropTypes.func.isRequired,
  value: PropTypes.string.isRequired,
};

const thumbnailStyle = { width: 100, height: 80, objectFit: 'cover' };
const ribbonStyle = { border: '1px solid #ccc', paddingTop: 2, paddingBottom: 2, fontSize: '95%' };

const getInstagramType = a => {
  if (a.trackedAsset.assetType === 'STORY') {
    return 'STORY';
  }

  if (a.isReel) {
    return 'REEL';
  }

  if (a.isIGTV) {
    return 'IGTV';
  }

  if (a.isVideo) {
    return 'VIDEO';
  }

  if (a.isCarousel) {
    return 'CAROUSEL';
  }

  return 'IMAGE';
};

const getFilter = platform => ({ trackedAsset: { platform } });

class AssetTable extends React.Component {
  //
  constructor(props) {
    super(props);
    this.state = {
      assetToBeDeleted: null,
      deleteDialogVisible: false,
      selectedAsset: null,
      selectedInsights: null,
      newAssets: [],
      facebookInsightsModalVisible: false,
      youtubeInsightsModalVisible: false,
      InstagramInsightsModalVisible: false,
      selectedStory: false,
      storyViewModalVisible: false,
      updatingAssetId: null,
    };
  }

  toggleFavoriteState = async ({ assetId, favourite, assetType }) => {
    const { restClient, toggleAssetLocalState } = this.props;
    const url = `${assetType === 'stories' ? 'stories' : 'trackedAssets'}/${assetId}`;
    await restClient.patch(url, {
      data: {
        attributes: {
          favourite: !favourite,
        },
      },
    });
    toggleAssetLocalState(assetId, !favourite);
  }

  componentWillMount() {
    const { assets } = this.props;
    const numAssets = assets ? assets.length : 0;
    const hasFacebook = assets && _.filter(assets, getFilter('FACEBOOK')).length > 0;
    const hasTwitter = assets && _.filter(assets, getFilter('TWITTER')).length > 0;
    const hasInstagram = assets && _.filter(assets, getFilter('INSTAGRAM')).length > 0;
    const isInstagramOnly = assets && _.filter(assets, getFilter('INSTAGRAM')).length === numAssets;
    const hasTiktok = assets && _.filter(assets, { platform: TIKTOK }).length > 0;

    this.setState({
      hasFacebook,
      hasTwitter,
      hasInstagram,
      isInstagramOnly,
      hasTiktok,
    });
  }

  refreshPage = () => {
    window.location.reload(true);
  }

  removeSupportingData = async ({ id, type, supportingData }) => {
    const { jsonAPIClient } = this.props;
    return jsonAPIClient.get(type, id).then(async response => {
      // eslint-disable-next-line no-restricted-syntax
      for (const data of supportingData) {
        // eslint-disable-next-line no-await-in-loop
        const toBeRemoved = await jsonAPIClient.get('supportingData', data.id);
        response.relationships('supportingData').remove(toBeRemoved);
      }
      await response.sync();
      this.refreshPage();
    });
  };

  SupportingDataCount = asset => {
    const { supportingData, id, type } = asset.trackedAsset || asset;
    const compacted = _.compact(supportingData);
    const ordered = _.reverse(_.sortBy(compacted, o => o.createdDate));

    if (ordered.length) {
      return (
        <Modal
          scrolling
          size="small"
          trigger={<Button
            size="mini"
            inverted
            circular
            content={ordered.length}
            color="green"
          />}
        >
          <Header icon="file image outline" content="Supporting files" />
          <Modal.Content>
            {
              _.map(ordered, (data, idx) => (

                <div style={imageWrapperStyle}>

                  <Image
                    src={`${S3_PREFIX}/${data.filePath}`}
                    size="small"
                    floated="left"
                  />

                  <div>
                    <p><a target="_blank" rel="noopener noreferrer" href={data.assetUrl}>{data.assetUrl}</a></p>
                    <p>impressions: {data.impressions}</p>
                    <p>reach: {data.reach}</p>
                    <p>views: {data.views}</p>
                    {
                      data.clicks && <p>clicks: {data.clicks}</p>
                    }
                    {
                      data.shares && <p>shares: {data.shares}</p>
                    }
                    {
                      data.saves && <p>saves: {data.saves}</p>
                    }
                    {
                      data.stickerTaps && <p>sticker taps: {data.stickerTaps}</p>
                    }

                    <p>---</p>
                    <p>Created By</p>
                    <p>{data.createdBy}</p>
                    <p>{moment(data.createdData).format('YYYY-DD-MM')}</p>

                    { idx === 0 &&
                      <h5 style={{ color: 'orange', paddingTop: 20 }}>
                        This is the LATEST supporting data and will be used in the reports
                      </h5>
                    }
                  </div>

                  <div style={{ clear: 'left' }} />
                  <Divider />
                </div>
              ))
            }
          </Modal.Content>
          <Modal.Actions
            style={removeActionStyles}
            className="align-center"
            onClick={() => { this.removeSupportingData({ id, type, supportingData }); }}
          >
            <h4>remove all supporting data</h4>
          </Modal.Actions>
          <Modal.Actions className="align-center">
            <h4>press ESCAPE to close</h4>
          </Modal.Actions>
        </Modal>
      );
    }
    return null;
  };

  openDeleteDialog = asset => {
    this.setState(prevState => ({
      deleteDialogVisible: !prevState.deleteDialogVisible,
      assetToBeDeleted: asset,
    }));
  }

  closeDeleteDialog = () => this.setState({
    deleteDialogVisible: false,
  })

  toggleUploadModal = () => {
    const { uploadSupportingVisible } = this.state;
    this.setState({ uploadSupportingVisible: !uploadSupportingVisible });
  }
  toggleAddStoryModal = () => {
    const { addStoryVisible } = this.state;
    this.setState({ addStoryVisible: !addStoryVisible });
  }

  toggleStoryViewModal = (id, inDeleteMode = false) => {
    this.setState({
      selectedStory: id,
      storyViewModalVisible: true,
      storyViewModelInDeleteMode: inDeleteMode,
    });
  }

  toggleFacebookInsightsModal = insights => {
    const { facebookInsightsModalVisible } = this.state;
    this.setState({
      facebookInsightsModalVisible: !facebookInsightsModalVisible,
      selectedInsights: insights,
    });
  }

  toggleYoutubeInsightsModal = insights => {
    const { youtubeInsightsModalVisible } = this.state;
    this.setState({
      youtubeInsightsModalVisible: !youtubeInsightsModalVisible,
      selectedInsights: insights,
    });
  }

  toggleInstagramInsightsModal = (insights, post) => {
    const { instagramInsightsModalVisible } = this.state;
    this.setState({
      instagramInsightsModalVisible: !instagramInsightsModalVisible,
      selectedInsights: insights,
      selectedPost: post,
    });
  }

  supportFilesHandler = value => {
    console.log('VA', value);
    const selectedAsset = value.trackedAsset || value;
    console.log('VA SELECTED', selectedAsset);
    // selectedAsset.text = _.get(value, 'text', _.get(value, 'extraData.text', 'No caption'));
    // const selectedAssetPostType = selectedAsset.postType || selectedAsset.isVideo ? 'video' : 'photo';
    this.setState({
      selectedAsset,
      // selectedAssetPostType,
    }, this.toggleUploadModal);
  }

  addSupportingFile = result => {
    const { selectedAsset } = this.state;
    selectedAsset.supportingData.push(result);
  }

  getCloudFrontUrl = rawUrl => {
    const updatedSavedThumbnailUrl = _.replace(rawUrl,
      'https://s3-eu-west-1.amazonaws.com/dyzio-stories/',
      'https://ri.dyzio.co/',
    );

    return updatedSavedThumbnailUrl;
  }

  getRow = (a, deleteHandler, influencer) => {
    const assetPropShape = {
      ...a,
      trackedAsset: a,
    };

    const { instagramAuthorised, youtubeAuthorised, facebookAuthorised } = influencer;
    if (_.get(a, 'platform') === VK) {
      return (
        <VKAsset
          asset={a}
          favouriteStateHandler={this.toggleFavoriteState}
          uploadSupportingDataComponent={
            <UploadSupportingFiles
              value={assetPropShape}
              handler={this.supportFilesHandler}
            />
          }
          supportingDataComponent={
            this.SupportingDataCount(a)
          }
        />
      );
    }
    if (_.get(a, 'platform') === SNAPCHAT) {
      return (
        <SnapChatAsset
          asset={a}
          favouriteStateHandler={this.toggleFavoriteState}
          uploadSupportingDataComponent={
            <UploadSupportingFiles
              value={assetPropShape}
              handler={this.supportFilesHandler}
            />
          }
          supportingDataComponent={
            this.SupportingDataCount(a)
          }
        />
      );
    }
    if (a.platform === 'BLOG') {
      return (
        <BlogAsset
          asset={a}
          favouriteStateHandler={this.toggleFavoriteState}
          uploadSupportingDataComponent={
            <UploadSupportingFiles
              value={assetPropShape}
              handler={this.supportFilesHandler}
            />
          }
          supportingDataComponent={
            this.SupportingDataCount(a)
          }
        />
      );
    }
    if (a.platform === TIKTOK) {
      const { initialData, savedThumbnailUrl, createdDate } = a;
      let tiktokData;
      let tikTokEngs;
      if (a.tiktokData) {
        const tiktokDataByDate = _.sortBy(a.tiktokData, 'createdDate');
        tiktokData = _.last(tiktokDataByDate);
        tikTokEngs = tiktokData.shareCount + tiktokData.diggCount + tiktokData.commentCount;
      }
      const daysSinceCreated = moment().diff(createdDate, 'days');
      const pendingThumbnail = daysSinceCreated < TIKTOK_THUMBNAIL_RETRY;
      const imageSrc = savedThumbnailUrl && this.getCloudFrontUrl(savedThumbnailUrl);
      const noImageComponent = pending => (pending
        ? <div style={{ paddingBottom: 3 }}><Icon name="hourglass empty" size="large" color="orange" /></div>
        : <div style={{ paddingBottom: 3 }}><Icon name="image empty" size="large" />No image available</div>);
      return (
        <Table.Row>
          <Table.Cell>
            { imageSrc
              ? <img src={imageSrc} style={thumbnailStyle} alt="asset thumbnail" />
              : noImageComponent(pendingThumbnail)
            }
            <h4 style={ribbonStyle}><TiktokIcon size="large" />TIKTOK</h4>
            <div>
              {a.publishedDate && moment(a.publishedDate).format('YYYY-MM-DD')}
            </div>
          </Table.Cell>
          <Table.Cell textAlign="left">
            { initialData ? initialData.text || initialData.desc : 'Data and thumbnail being fetched from TikTok'}
          </Table.Cell>
          <Table.Cell>
            <div>views: { tiktokData ? sf(tiktokData.playCount) : 'OPEN CR' }</div>
            <div>
              engagements:{ tiktokData ? sf(tikTokEngs) : 'OPEN CR' }
            </div>
            <div><Icon name="minus" color="grey" /></div>
          </Table.Cell>
          <Table.Cell><Icon name="circle thin" color="grey" /></Table.Cell>
          <Table.Cell>
            <FavouriteStar asset={a} onClick={this.toggleFavoriteState} />
          </Table.Cell>
          <Table.Cell />
          <Table.Cell />
          <Table.Cell>
            <DeleteButton
              handler={() => this.openDeleteDialog({
                thumbnail: a.savedThumbnailUrl,
                trackedAsset: a,
                text: initialData.text,
                ...a,
              })}
            />
          </Table.Cell>
        </Table.Row>
      );
    }

    if (a.platform === TELEGRAM) {
      console.log(a.initialData);

      if (a.initialData) {
        const { text, thumbnail, views, forwards, replies } = a.initialData;
        const trimmedText = _.truncate(text, { length: 200, separator: ' ' });
        const imageSrc = `https://pics.dyzio.co/${thumbnail}`;
        return (
          <Table.Row>
            <Table.Cell>
              <img src={imageSrc} style={thumbnailStyle} alt="asset thumbnail" />
              <h4 style={ribbonStyle}><Icon name="telegram plane" size="large" color="blue" />TELEGRAM</h4>
            </Table.Cell>
            <Table.Cell textAlign="left">
              <p style={{ height: 70, overFlowY: 'hidden', verticalAlign: 'middle' }}>{trimmedText}</p>
              <p style={{ textAlign: 'center' }}>
                <a target="_blank" rel="noopener noreferrer" href={a.assetURL}>{a.assetURL}</a>
              </p>
            </Table.Cell>
            <Table.Cell>
              <div>views: { sf(views) }</div>
              <div>forwards: { sf(forwards) }</div>
              <div>replies: { sf(replies) }</div>
            </Table.Cell>
            <Table.Cell><Icon name="circle thin" color="grey" /></Table.Cell>
            <Table.Cell>
              <FavouriteStar asset={a} onClick={this.toggleFavoriteState} />
            </Table.Cell>
            <Table.Cell />
            <Table.Cell />
            <Table.Cell>
              <DeleteButton
                handler={() => this.openDeleteDialog({
                  thumbnail: imageSrc,
                  trackedAsset: a,
                  text: a.initialData.text,
                  ...a,
                })}
              />
            </Table.Cell>
          </Table.Row>
        );
      }
      return (
        <Table.Row>
          <Table.Cell>
            <div style={{ paddingBottom: 3 }}><Icon name="hourglass empty" size="large" color="orange" /></div>
            <h4 style={ribbonStyle}><Icon name="telegram plane" size="large" color="blue" />TELEGRAM</h4>
          </Table.Cell>
          <Table.Cell textAlign="left">
          loading...
          </Table.Cell>
          <Table.Cell>
          loading...
          </Table.Cell>
          <Table.Cell><Icon name="circle thin" color="grey" /></Table.Cell>
          <Table.Cell>
            -
          </Table.Cell>
          <Table.Cell />
          <Table.Cell />
          <Table.Cell>
            -
          </Table.Cell>
        </Table.Row>
      );
    }
    if (a.notFound) {
      return (
        <Table.Row>
          <Table.Cell>
            <Icon name={_.toLower(a.trackedAsset.platform)} color="red" />
          </Table.Cell>
          <Table.Cell textAlign="left">
            <p>
              this post has been removed from {a.trackedAsset.platform}
            </p>
          </Table.Cell>
          <Table.Cell>{ assetWarning() }</Table.Cell>
          <Table.Cell>{ assetWarning() }</Table.Cell>
          <Table.Cell>{ assetWarning() }</Table.Cell>
          <Table.Cell>{ assetWarning() }</Table.Cell>
          <Table.Cell>{ assetWarning() }</Table.Cell>
          <Table.Cell>
            <DeleteButton
              handler={() => this.openDeleteDialog({
                ...a,
              })}
            />
          </Table.Cell>
        </Table.Row>
      );
    }
    if (a.withError) {
      return (
        <Table.Row>
          <Table.Cell>
            <Icon name={_.toLower(a.trackedAsset.platform)} color="red" />
          </Table.Cell>
          <Table.Cell textAlign="left">
            <p>
            Loading asset...
            &nbsp;
              <TargetLink
                href={a.trackedAsset.assetURL}
                renderIf
              >
                view asset <Icon name="share external" />
              </TargetLink>
            </p>
          </Table.Cell>
          <Table.Cell>{ assetError() }</Table.Cell>
          <Table.Cell>{ assetError() }</Table.Cell>
          <Table.Cell>{ assetError() }</Table.Cell>
          <Table.Cell>{ assetError() }</Table.Cell>
          <Table.Cell>{ assetError() }</Table.Cell>
          <Table.Cell>
            <DeleteButton
              handler={() => this.openDeleteDialog({
                ...a,
              })}
            />
          </Table.Cell>
        </Table.Row>
      );
    }
    if (a.state === FETCHING) {
      return (
        <Table.Row>
          <Table.Cell>
            <Icon name={_.toLower(a.trackedAsset.platform)} color="orange" />
          </Table.Cell>
          <Table.Cell>{ fetchingData() }</Table.Cell>
          <Table.Cell>{ fetchingData() }</Table.Cell>
          <Table.Cell>{ fetchingData() }</Table.Cell>
          <Table.Cell>{ fetchingData() }</Table.Cell>
          <Table.Cell>{ fetchingData() }</Table.Cell>
          <Table.Cell>{ fetchingData() }</Table.Cell>
          <Table.Cell>{ fetchingData() }</Table.Cell>
        </Table.Row>
      );
    }

    // MANUALLY ADDED STORY
    if (a.trackedAsset.assetType === 'STORY') {
      let impressions;
      let engagements;
      let thumbnail;
      const { supportingData } = a.trackedAsset;
      if (supportingData && !isGraphQLEmpty(supportingData)) {
        const filtered = _.filter(supportingData, 'createdDate'); // remove the GraphQL null at [0]
        const ordered = _.reverse(_.sortBy(filtered, o => o.createdDate));
        const latestSupportingData = ordered[0];
        if (latestSupportingData) {
          impressions = latestSupportingData.impressions;
          engagements = latestSupportingData.engagements;
          thumbnail = `${S3_PREFIX}/${latestSupportingData.filePath}`;
        }
      }
      return (
        <Table.Row>
          <Table.Cell>
            <h4 style={ribbonStyle}>
              <Icon name="instagram" color="pink" size="large" />
              { getInstagramType(a)}
            </h4>
          </Table.Cell>
          <Table.Cell textAlign="left">{ a.text }</Table.Cell>
          <Table.Cell>
            <div>views: { sf(a.latestStatistic.openCount) }</div>
            <div>engagements: { sf(engagements) }</div>
            <div>{ sf(impressions) }</div>
          </Table.Cell>
          <Table.Cell><Icon name="circle thin" color="grey" /></Table.Cell>
          <Table.Cell>
            <FavouriteStar asset={a} onClick={this.toggleFavoriteState} />
          </Table.Cell>
          <Table.Cell><UploadSupportingFiles value={a} handler={this.supportFilesHandler} /></Table.Cell>
          <Table.Cell>{this.SupportingDataCount(a)}</Table.Cell>
          <Table.Cell>
            <DeleteButton
              handler={() => this.openDeleteDialog({
                thumbnail,
                ...a,
              })}
            />
          </Table.Cell>
        </Table.Row>
      );
    }

    switch (a.trackedAsset.platform) {
    case 'YOUTUBE': {
      const latestStatistic = _.last(a.statistics);
      return (
        <Table.Row>
          <Table.Cell>
            <img src={a.thumbnail} style={thumbnailStyle} alt="asset thumbnail" />
            <h4 style={ribbonStyle}>
              YOUTUBE VIDEO
            </h4>
          </Table.Cell>
          <Table.Cell textAlign="left">{ a.title }</Table.Cell>
          <Table.Cell>
            <div>views: { sf(latestStatistic.viewCount) }</div>
            <div>likes: { sf(latestStatistic.likeCount) }</div>
            <div style={{ width: 120, margin: '0 auto' }}>
              {
                youtubeAuthorised
                  ? <YoutubeImpressions
                    latestStatistic={a.latestStatistic}
                    popupToggleHandler={this.toggleYoutubeInsightsModal}
                  />
                  : <PrivateDataComponent />
              }
            </div>
          </Table.Cell>
          <Table.Cell><Icon name="circle thin" color="grey" /></Table.Cell>
          <Table.Cell>
            <FavouriteStar asset={a} onClick={this.toggleFavoriteState} />
          </Table.Cell>
          <Table.Cell><UploadSupportingFiles value={a} handler={this.supportFilesHandler} /></Table.Cell>
          <Table.Cell>{this.SupportingDataCount(a)}</Table.Cell>
          <Table.Cell>
            <DeleteButton value={a} handler={() => this.openDeleteDialog(a)} />
          </Table.Cell>
        </Table.Row>
      );
    }
    case 'INSTAGRAM':
    {
      let impressions;
      const { supportingData, savedThumbnailUrl, assetId } = a.trackedAsset;
      if (supportingData && !isGraphQLEmpty(supportingData)) {
        const filtered = _.filter(supportingData, 'createdDate'); // remove the GraphQL null at [0]
        const ordered = _.reverse(_.sortBy(filtered, o => o.createdDate));
        const latestSupportingData = ordered[0];
        if (latestSupportingData) {
          impressions = latestSupportingData.impressions;
        }
      }

      const imageSrc = savedThumbnailUrl && this.getCloudFrontUrl(savedThumbnailUrl);
      const imageSrcX = `https://pics.dyzio.co/${assetId}.jpg`;

      const trimmedText = _.truncate(a.text, { length: 200, separator: ' ' });

      return (
        <Table.Row>
          <Table.Cell>
            { imageSrc
              ? <img src={imageSrc} style={thumbnailStyle} alt="asset thumbnail" />
              : <img src={imageSrcX} style={thumbnailStyle} alt="asset thumbnail" />
            }
            <h4 style={ribbonStyle}>
              <Icon name="instagram" color="pink" size="large" />
              { getInstagramType(a)}
            </h4>
            <div>
              {a.publishedDate && moment(a.publishedDate).format('YYYY-MM-DD')}
            </div>
          </Table.Cell>
          <Table.Cell textAlign="left">
            <p style={{ height: 70, overFlowY: 'hidden', verticalAlign: 'middle' }}>{trimmedText}</p>
            <p style={{ textAlign: 'center' }}>
              <Link to={`/asset/post/${a.trackedAsset.id}`}>view full details...</Link>
            </p>
          </Table.Cell>
          <Table.Cell>
            <div>views: { getViews(a) }</div>
            <div>engagements: { sf(_.add(a.latestStatistic.likeCount, a.latestStatistic.commentCount)) }</div>
            <div style={{ width: 120, margin: '0 auto' }}>
              {
                instagramAuthorised && !a.isIGTV &&
                <InstagramImpressions
                  instagramAuthorised={instagramAuthorised}
                  latestStatistic={a.latestStatistic}
                  supportingData={a.trackedAsset.supportingData}
                  popupToggleHandler={this.toggleInstagramInsightsModal}
                  post={a}
                />
              }
              {
                !instagramAuthorised && !a.isIGTV && !impressions && <PrivateDataComponent />
              }
              {
                a.isIGTV &&
                <NotAvailableComponent
                  metricName="impressions"
                  platform="Instagram"
                  assetType="IGTV"
                />
              }
              {
                !instagramAuthorised && !a.isIGTV && impressions && sf(impressions)
              }
            </div>
          </Table.Cell>
          <Table.Cell>
            {
              a.linkedAds
                ? <span><Icon name="facebook" color="blue" />{ a.linkedAds.length }</span>
                : <Icon name="circle thin" color="grey" />
            }
          </Table.Cell>
          <Table.Cell>
            <FavouriteStar asset={a} onClick={this.toggleFavoriteState} />
          </Table.Cell>
          <Table.Cell><UploadSupportingFiles value={a} handler={this.supportFilesHandler} /></Table.Cell>
          <Table.Cell>{this.SupportingDataCount(a)}</Table.Cell>
          <Table.Cell>
            <DeleteButton handler={() => this.openDeleteDialog(a)} />
          </Table.Cell>
        </Table.Row>
      );
    }
    case 'TWITTER': {
      console.log(a);
      const tweetMedia = _.get(a, 'entities.media') || [];
      const mainTweetMedia = _.head(tweetMedia);
      const tweetThumbnail = _.get(mainTweetMedia, 'media_url');
      const mediaUrl = _.get(mainTweetMedia, 'expanded_url');
      let assetType = 'post';

      if (_.includes(mediaUrl, 'video')) {
        assetType = 'video';
      }
      if (_.includes(mediaUrl, 'photo')) {
        assetType = 'image';
      }
      const stat = a.latestStatistic;
      const engagements = stat.likeCount + (stat.commentCount || 0);
      return (
        <Table.Row>
          <Popup
            content="Twitter Post" trigger={
              <Table.Cell style={{ height: 90 }}>
                { tweetThumbnail
                  ? <img src={tweetThumbnail} style={thumbnailStyle} alt="asset thumbnail" />
                  : <Icon name={_.toLower(a.trackedAsset.platform)} color="blue" />
                }
                <h4 style={ribbonStyle}>
                  <Icon name="twitter" color="blue" size="large" />
                  {assetType}
                </h4>
              </Table.Cell>
            }
          />
          <Table.Cell textAlign="left">{ a.text }</Table.Cell>
          <Table.Cell>
            <div>views: { <PrivateDataComponent /> }</div>
            <div>engagements: { sf(engagements) }</div>
            <div>{ <PrivateDataComponent /> }</div>
          </Table.Cell>
          <Table.Cell><Icon name="circle thin" color="grey" /></Table.Cell>
          <Table.Cell>
            <FavouriteStar asset={a} onClick={this.toggleFavoriteState} />
          </Table.Cell>
          <Table.Cell><UploadSupportingFiles value={a} handler={this.supportFilesHandler} /></Table.Cell>
          <Table.Cell>{this.SupportingDataCount(a)}</Table.Cell>
          <Table.Cell>
            <DeleteButton value={a} handler={() => this.openDeleteDialog(a)} />
          </Table.Cell>
        </Table.Row>
      );
    }
    case 'FACEBOOK':
      return (
        <Table.Row>
          <Table.Cell>
            <Icon name={_.toLower(a.trackedAsset.platform)} color="blue" />
            {
              <FacebookPostType asset={a} />
            }
          </Table.Cell>
          <Table.Cell textAlign="left">{ a.text }</Table.Cell>
          <Table.Cell>
            <div>views: { sf(a.latestStatistic.viewCount) }</div>
            <div>engagements:{ sf(a.latestStatistic.likeCount) }</div>
            <div style={{ width: 120, margin: '0 auto' }}>
              {
                facebookAuthorised
                  ? <FacebookImpressions
                    latestStatistic={a.latestStatistic}
                    popupToggleHandler={this.toggleFacebookInsightsModal}
                  />
                  : <PrivateDataComponent />
              }
            </div>
          </Table.Cell>
          <Table.Cell><Icon name="circle thin" color="grey" /></Table.Cell>
          <Table.Cell>
            <FavouriteStar asset={a} onClick={this.toggleFavoriteState} />
          </Table.Cell>
          <Table.Cell><UploadSupportingFiles value={a} handler={this.supportFilesHandler} /></Table.Cell>
          <Table.Cell>{this.SupportingDataCount(a)}</Table.Cell>
          <Table.Cell>
            <DeleteButton value={a} handler={() => this.openDeleteDialog(a)} />
          </Table.Cell>
        </Table.Row>
      );
    default:
      return null;
    }
  };

  getStoryRow = props => {
    const { deleteAssetConfirmOpen } = this.state;
    if (!props) return null;
    const { supportingData, savedThumbnailUrl, publishedDate } = props;
    if (!publishedDate) return null;
    const latestSupportingData = _.last(supportingData);
    const imageSrc = savedThumbnailUrl && this.getCloudFrontUrl(savedThumbnailUrl);
    const repostedFrom = _.get(props, 'repostedFrom');
    const storyLinks = _.get(props, 'storyLinks');

    const storyFeedMedia = _.get(props, 'initialData.story_feed_media');

    const isReelsRepost = storyFeedMedia && storyFeedMedia[0].product_type === 'clips';

    return (
      <Table.Row>
        <Table.Cell>

          { imageSrc
            ? <img src={imageSrc} style={thumbnailStyle} alt="asset thumbnail" />
            : <div style={{ paddingBottom: 3 }}><Icon name="hourglass empty" size="large" color="orange" /></div>
          }
          <h4 style={ribbonStyle}>
            <Icon name="instagram" color="pink" size="large" />
            STORY&nbsp;
            {
              props.assetType === 'VIDEO'
                ? <Icon name="video play" color="pink" />
                : <Icon name="image" color="pink" />
            }
            {
              isReelsRepost && <span>REELS</span>
            }
          </h4>
          <div>
            { publishedDate && moment(publishedDate).format('YYYY-MM-DD')}
          </div>
        </Table.Cell>
        <Table.Cell textAlign="left">
          <p>CAPTION: {_.get(props, 'caption', '-')}</p>
          <p>HASHTAGS: {_.get(props, 'hashtags', '-')}</p>
          <p>MENTIONS: {_.get(props, 'mentions', '-')}</p>
          {
            repostedFrom &&
            <p><Label>repost from {repostedFrom}</Label></p>
          }
          {
            storyLinks &&
            <p>LINK: <a href={`https://${_.head(storyLinks).displayUrl}`} target="_blank">{_.head(storyLinks).displayUrl}</a></p>
          }
          <p style={{ textAlign: 'center' }}><Link to={`/asset/story/${props.id}`}>view full details...</Link></p>
        </Table.Cell>
        <Table.Cell>
          <div style={{ width: 120, margin: '0 auto' }}>
            <Button
              color="pink"
              labelPosition="left"
              fluid
              inverted
              onClick={() => this.toggleStoryViewModal(props.id)}
              size="small"
              icon
            >
              <Icon name="pie graph" />
              {sf(_.get(latestSupportingData, 'views') || _.get(latestSupportingData, 'impressions') || props.latestImpressions)}
            </Button>

          </div>
        </Table.Cell>
        <Table.Cell>
          {
            props.linkedAds
              ? <span><Icon name="facebook" color="blue" />{ props.linkedAds.length }</span>
              : <Icon name="circle thin" color="grey" />
          }
        </Table.Cell>
        <Table.Cell>
          <FavouriteStar asset={props} onClick={this.toggleFavoriteState} />
        </Table.Cell>

        <Table.Cell><UploadSupportingFiles value={props} handler={this.supportFilesHandler} /></Table.Cell>
        <Table.Cell>{this.SupportingDataCount(props)}</Table.Cell>
        <Table.Cell>
          <DeleteButton
            handler={() => this.openDeleteDialog({
              thumbnail: props.savedThumbnailUrl,
              text: props.caption,
              ...props,
            })}
            value={props.id}
          />
        </Table.Cell>
      </Table.Row>
    );
  }

  onDeleteDialogConfirmedCallback = () => {
    const { assetToBeDeleted } = this.state;
    if (isAutoAdded(assetToBeDeleted.createdBy) && isStory(assetToBeDeleted.type)) {
      this.props.deleteStoryHandler(assetToBeDeleted.id);
    } else {
      console.log(assetToBeDeleted);
      this.props.deleteAssetHandler({}, {
        value: assetToBeDeleted,
      });
    }
    this.setState({
      assetToBeDeleted: null,
    });
  }

  closeViewStoryModal = () => {
    this.setState({
      selectedStory: null,
      storyViewModalVisible: false,
    });
  }

  render = () => {
    const {
      assets,
      deleteAssetHandler,
      influencer,
      stories,
      campaignName,
      campaignType,
      hashtags,
    } = this.props;
    const {
      selectedAsset,
      selectedAssetPostType,
      selectedInsights,
      selectedPost,
      uploadSupportingVisible,
      facebookInsightsModalVisible,
      youtubeInsightsModalVisible,
      instagramInsightsModalVisible,
      newAssets,
      selectedStory,
      storyViewModalVisible,
      storyViewModelInDeleteMode,
      deleteDialogVisible,
      assetToBeDeleted,
    } = this.state;

    const hasNoAnalytics = asset => _.includes(PLATFORMS_WITHOUT_ANALYTICS, asset.platform);
    const trackedAssetsWithoutAnalytics = _.reject(assets, asset => hasNoAnalytics(asset));
    const tiktokAssets = _.filter(assets, ['platform', TIKTOK]);
    const vkAssets = _.filter(assets, ['platform', VK]);
    const blogAssets = _.filter(assets, ['platform', BLOG]);
    const snapChatAssets = _.filter(assets, ['platform', SNAPCHAT]);
    const telegramAssets = _.filter(assets, ['platform', TELEGRAM]);

    const linkedStories = _.filter(stories, 'publishedDate');

    const assetsWithoutAnalytics = _.uniqBy(_.compact(_.concat(trackedAssetsWithoutAnalytics, newAssets)), '_id');
    const mergedAssets = _.concat(assetsWithoutAnalytics, tiktokAssets, vkAssets, blogAssets, snapChatAssets, telegramAssets);

    console.log('mergedAssets', mergedAssets);

    console.log('stories', stories);

    const hasAssets = mergedAssets.length > 0 || linkedStories.length > 0;

    const inherit = 'inherit';
    const isWideCard = true; // campaignType === 'WORKFLOW';
    const inheritFont = {
      fontFamily: inherit,
      fontSize: inherit,
      fontWeight: inherit,
    };

    const instagramPostsCount = _.filter(mergedAssets, { trackedAsset: { platform: INSTAGRAM } }).length;
    const tweetsCount = _.filter(mergedAssets, { trackedAsset: { platform: TWITTER } }).length;
    const youtubeCount = _.filter(mergedAssets, { trackedAsset: { platform: YOUTUBE } }).length;
    const facebookCount = _.filter(mergedAssets, { trackedAsset: { platform: FACEBOOK } }).length;
    const tiktokCount = _.filter(mergedAssets, { platform: TIKTOK }).length;
    const telegramCount = _.filter(mergedAssets, { platform: TELEGRAM }).length;

    return (
      <div>
        {
          hasAssets &&
          <div>
            <div>
              <Label color="pink" basic><Icon name="instagram" size="large" />
                POSTS: {instagramPostsCount}
              </Label>
              <Label color="pink" basic><Icon name="instagram" size="large" />
                STORIES: {linkedStories.length}
              </Label>
              <Label color="black" basic><Icon name="music" size="large" />TIKTOKS: {tiktokCount}</Label>
              <Label color="blue" basic><Icon name="twitter" size="large" />TWEETS: {tweetsCount}</Label>
              <Label color="red" basic>YOUTUBE: {youtubeCount}</Label>
              <Label color="blue" basic><Icon name="facebook" size="large" />FACEBOOK: {facebookCount}</Label>
              <Label color="blue" basic><Icon name="telegram plane" size="large" />TELEGRAM: {telegramCount}</Label>
            </div>
            <Table celled fixed basic="very" style={assetTableStyles} size="small" textAlign="center">
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell width={2}>{isWideCard && 'channel'}</Table.HeaderCell>
                  <Table.HeaderCell textAlign="left">title</Table.HeaderCell>
                  <Table.HeaderCell width={3}>
                  metrics
                  </Table.HeaderCell>
                  <Table.HeaderCell width={1}>
                    <Popup
                      trigger={
                        <span style={inheritFont}>Ads</span>
                      }
                      content={'Facebook Ads linked to post'}
                    />
                  </Table.HeaderCell>
                  <Table.HeaderCell width={isWideCard ? 1 : 2}>
                    <Popup
                      trigger={<Icon name="star" disabled />}
                      content="Click here to favourite the post - this will add it to the campaign highlights section in your shared reports"
                    />
                  </Table.HeaderCell>
                  <Table.HeaderCell width={isWideCard ? 1 : 2}>
                    <Popup
                      trigger={<span style={inheritFont}>add data</span>}
                      content="upload supporting files/manually add insights data."
                    />
                  </Table.HeaderCell>

                  <Table.HeaderCell width={1}>
                    <Popup
                      trigger={<Icon name="file outline" color="grey" />}
                      content="supporting files?"
                    />
                  </Table.HeaderCell>

                  <Table.HeaderCell width={1}>{isWideCard && 'remove'}</Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {
                  _.map(mergedAssets, asset => (this.getRow(asset, deleteAssetHandler, influencer)))
                }
                {
                  _.map(linkedStories, story => this.getStoryRow(story))
                }
              </Table.Body>
            </Table>
          </div>
        }

        {
          !hasAssets &&
          <NoAssetsCard />
        }

        {
          uploadSupportingVisible && <UploadModal
            asset={selectedAsset}
            popupToggleHandler={this.toggleUploadModal}
            successHandler={this.addSupportingFile}
          />
        }

        {
          deleteDialogVisible &&
          <DeleteAssetDialog
            onClose={this.closeDeleteDialog}
            onDelete={this.onDeleteDialogConfirmedCallback}
            highlighedHashtags={transformHashtagstIntoArray(hashtags)}
            {...assetToBeDeleted}
          />
        }

        {
          storyViewModalVisible &&
          <InstagramStoryModal
            onClose={this.closeViewStoryModal}
            selectedStory={selectedStory}
            campaignName={campaignName}
          />
        }

        <FacebookInsightsModal
          latestStatistic={selectedInsights}
          open={facebookInsightsModalVisible}
          popupToggleHandler={this.toggleFacebookInsightsModal}
        />

        <YoutubeInsightsModal
          latestStatistic={selectedInsights}
          open={youtubeInsightsModalVisible}
          popupToggleHandler={this.toggleYoutubeInsightsModal}
        />

        <InstagramInsightsModal
          latestStatistic={selectedInsights}
          open={instagramInsightsModalVisible}
          popupToggleHandler={this.toggleInstagramInsightsModal}
          post={selectedPost}
          hashtags={hashtags}
        />
      </div>
    );
  }
}

AssetTable.propTypes = {
  assets: PropTypes.object.isRequired,
  deleteAssetHandler: PropTypes.func.isRequired,
  influencer: PropTypes.object.isRequired,
  stories: PropTypes.array,
  jsonAPIClient: PropTypes.object.isRequired,
};

AssetTable.defaultProps = {
  stories: [],
  assets: [],
};

export default compose(
  hasRESTClient,
  hasJSONAPIClient,
)(AssetTable);



/** WEBPACK FOOTER **
 ** ./src/components/asset-table/index.js
 **/